/**
 * Port concettuale da Pulse `promptBuilder.ts` (ig-carousel).
 * F2-rev: outline ricca con bullets, visual strutturato, no dati inventati.
 */
import { presentationTypeGuidance, slideRangeForPresentationType } from "./presentationType.js";
/** Schema outline ricca per deck 16:9 (F2-rev). */
export const SURF_SPLIT_SYSTEM = [
    "Sei un redattore editoriale specializzato in presentazioni professionali (deck slide 16:9).",
    "Ricevi materiale sorgente in Markdown e lo trasformi in una struttura slide ricca e fedele al sorgente.",
    "Rispondi ESCLUSIVAMENTE con un blocco JSON fenced (```json ... ```) senza testo attorno.",
    "Lo schema è:",
    "{",
    '  "slides": [',
    "    {",
    '      "role": "cover"|"agenda"|"content"|"section"|"comparison"|"data"|"quote"|"summary"|"cta",',
    '      "title": "titolo slide, frase forte",',
    '      "subtitle": "sottotitolo opzionale",',
    '      "bullets": ["punto 1 sostanziale (claim + dettaglio)", "punto 2", "..."],',
    '      "body": "paragrafo opzionale solo se serve testo continuo",',
    '      "notes": "speaker notes opzionali",',
    '      "visual": {',
    '        "kind": "none"|"image"|"graphic"|"chart"|"table",',
    '        "caption": "didascalia opzionale",',
    '        "imageSubject": "soggetto per illustrazione se kind=image",',
    '        "graphic": { "template":"list"|"process"|"cycle"|"pyramid"|"comparison", "items":[{"label":"...","sublabel":"..."}] },',
    '        "chart": { "type":"bar"|"line"|"pie", "series":[{"label":"...","points":[{"x":"...","y":0}]}] },',
    '        "table": { "columns":["..."], "rows":[["..."]] }',
    "      },",
    '      "layoutHint": "es. cover, bullets, big-number, quote, image-left, comparison, two-column",',
    '      "intent": "breve scopo editoriale della slide"',
    "    }",
    "  ],",
    '  "voice": "editorial | executive | technical | inspirational | ..."',
    "}",
    "Regole obbligatorie:",
    "- Prima slide: role=cover con title accattivante.",
    "- Slide content/section/comparison/data: 3–6 bullets SOSTANZIALI ciascuno (claim + dettaglio), non una singola frase.",
    "- Estrai struttura reale dal sorgente: liste→bullets, confronti→role=comparison, citazioni→role=quote, statistiche→role=data + layoutHint big-number.",
    "- Max 5 bullet per slide; una idea per slide — titoli brevi, bullet paralleli e concisi.",
    "- DATI NUMERICI: chart o table SOLO se i valori sono ESPLICITAMENTE presenti nel sorgente. VIETATO inventare numeri.",
    "- Se il sorgente non contiene dati quantitativi verificabili, usa visual.kind=image, graphic o none — mai chart/table fabbricati.",
    "- Mantieni lingua e tono del materiale sorgente.",
    "- layoutHint coerente con role (comparison→comparison, quote→quote, data KPI→big-number, immagine→image-left).",
    "- Rispetta il numero di slide indicato nel prompt utente.",
].join("\n");
export const SURF_SLIDE_TARGET_MIN = 1;
export const SURF_SLIDE_TARGET_MAX = 40;
export function clampExplicitSlideTarget(n) {
    return Math.min(SURF_SLIDE_TARGET_MAX, Math.max(SURF_SLIDE_TARGET_MIN, Math.round(n)));
}
export function surfSplitUserPrompt(sourceTitle, sourceBody, opts) {
    const title = (sourceTitle || "").trim();
    const body = (sourceBody || "").trim();
    const range = slideRangeForPresentationType(opts?.presentationType);
    const guidance = presentationTypeGuidance(opts?.presentationType);
    const lines = [
        title ? `Titolo sorgente: ${title}` : "Titolo sorgente: (senza titolo)",
        "",
        "Materiale sorgente (Markdown):",
        body.slice(0, 48_000),
        "",
        guidance,
    ];
    if (opts?.slideTarget === "auto" || opts?.slideTarget == null) {
        lines.push("", `Numero slide: scegli automaticamente tra ${range.min} e ${range.max} in base alla lunghezza e ricchezza del materiale.`);
    }
    else if (typeof opts.slideTarget === "number") {
        const n = clampExplicitSlideTarget(opts.slideTarget);
        lines.push("", `Numero slide richiesto: genera ESATTAMENTE ${n} slide (né più né meno).`);
    }
    return lines.join("\n");
}
export function buildSourceCleanPrompt(raw) {
    const systemText = "Sei un editor. Ripulisci il testo sorgente da rumore (menu, footer, duplicati), " +
        "mantieni markdown e fatti utili per una presentazione. Rispondi SOLO con il testo pulito.";
    return {
        systemText,
        userText: `Sorgente:\n\n${raw.trim().slice(0, 48_000)}`,
    };
}
export function buildResearchChatPrompt(sourceText, question, history) {
    const transcript = history.length > 0
        ? history
            .map((m) => `${m.role === "user" ? "Utente" : "Assistente"}: ${m.content}`)
            .join("\n\n")
        : "(Prima domanda.)";
    const systemText = "Sei un assistente di ricerca per presentazioni. Usa SOLO il materiale sorgente fornito. " +
        "Se la risposta non è nel materiale, dillo. Rispondi in italiano, in modo conciso.";
    const userText = `Materiale sorgente:\n\n${sourceText.trim().slice(0, 24_000)}\n\n` +
        `Cronologia:\n${transcript}\n\n` +
        `Domanda:\n${question.trim()}`;
    return { systemText, userText };
}
/** Estrae il primo JSON object dalla risposta LLM (blocco ```json o prima `{...}`). */
export function extractJsonObject(text) {
    const t = String(text || "").trim();
    if (!t)
        return null;
    const fence = t.match(/```(?:json)?\s*([\s\S]*?)```/i);
    if (fence) {
        try {
            const parsed = JSON.parse(fence[1].trim());
            if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
                return parsed;
            }
        }
        catch {
            /* fallthrough */
        }
    }
    const firstBrace = t.indexOf("{");
    const lastBrace = t.lastIndexOf("}");
    if (firstBrace >= 0 && lastBrace > firstBrace) {
        try {
            const parsed = JSON.parse(t.slice(firstBrace, lastBrace + 1));
            if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
                return parsed;
            }
        }
        catch {
            /* fallthrough */
        }
    }
    return null;
}
/** Titolo euristico dalla prima riga H1 del markdown sorgente. */
export function inferSourceTitle(sourceText) {
    const m = sourceText.trim().match(/^#\s+(.+)$/m);
    return m?.[1]?.trim() ?? "";
}
